import { ResolvedPageReference, Space } from '@databases/types/confluence';

import { nextPagination } from '../pagination';

import { searchConfluence } from './confluence';
import { fetchConfluenceData, rejectOnError } from './fetch';
import { pageToReference, quoteCql, quoteFuzzyCql } from './utils';

async function getSpace(spaceKey: string, expand: string[] = []): Promise<Space> {
  const res = await fetchConfluenceData(
    `/rest/api/space/${encodeURIComponent(spaceKey)}?${new URLSearchParams({
      expand: expand.join(','),
    })}`
  ).then(rejectOnError);
  return await res.json();
}

export function getSpaces(expand: string[] = []): AsyncGenerator<Space, void, void> {
  return nextPagination<Space>(async (url) => {
    const res = await fetchConfluenceData(
      url ??
        `/rest/api/space?${new URLSearchParams({
          expand: expand.join(','),
          limit: '200',
        })}`
    ).then(rejectOnError);
    return await res.json();
  });
}

export interface SpaceInfo {
  key: string;
  name: string;
  homepage: ResolvedPageReference | undefined;
}

/**
 * @param spaceKey
 */
export async function getSpaceInfo(spaceKey: string): Promise<SpaceInfo> {
  const space = await getSpace(spaceKey, ['homepage']);

  return {
    key: space.key,
    name: space.name,
    homepage: space.homepage ? pageToReference({ ...space.homepage, space }) : undefined,
  };
}

async function* searchSpaces(
  cql: string,
  expand: string[] = [],
  limit = Infinity
): AsyncGenerator<Space, void, void> {
  for await (const result of searchConfluence<Space>(cql, expand, limit)) {
    yield result.space;
  }
}

export function searchSpacesByTitle(
  query: string,
  expand: string[] = [],
  limit = Infinity
): AsyncGenerator<Space, void, void> {
  return searchSpaces(
    `type=space and (title ~ ${quoteFuzzyCql(query)} or title ~ ${quoteFuzzyCql(
      `${query}*`
    )} or space = ${quoteCql(query)})`,
    expand,
    limit
  );
}

export function getRecentlyViewedSpaces(
  expand: string[] = [],
  limit = 20
): AsyncGenerator<Space, void, void> {
  return searchSpaces(
    `type=space and space in recentlyViewedSpaces(${limit})`,
    expand,
    limit
  );
}
